import { useRef, useState } from "react";
import { Camera, ClipboardText, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import { formatInches } from "@/lib/measure";

const SPLIT = { Double: 0.5, "60/40": 0.6, "40/60": 0.4, "70/30": 0.7, "30/70": 0.3 };

export const photoUrl = (path) =>
  path ? `${API}/photos/${path}?auth=${localStorage.getItem("sw_token")}` : "";

// Clean sink-only drawing: outline, bowls and faucet holes. No dimension lines.
export const CutoutThumb = ({ t, className = "h-10 w-16" }) => {
  const w = Math.max(Number(t.w) || 24, 1);
  const h = Math.max(Number(t.h) || 18, 1);
  const holes = Math.max(0, Math.min(Number(t.faucet_holes) || 0, 6));
  const spacing = Number(t.faucet_spacing) || 4;
  const pad = Math.max(w, h) * 0.14;
  const holeR = Math.max(w, h) * 0.03;
  const shape = t.shape || "Rectangle";
  const rx = shape === "Oval" || shape === "Round" ? Math.min(w, h) / 2 : shape === "D-shape" ? h * 0.35 : w * 0.05;
  const split = SPLIT[shape];
  const bowlPad = Math.min(w, h) * 0.12;
  const mid = w * (split || 0.5);
  const stroke = "#f5d78e";
  return (
    <svg
      viewBox={`${-pad} ${-pad - holeR * 4} ${w + pad * 2} ${h + pad * 2 + holeR * 4}`}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      data-testid="cutout-template-thumb"
    >
      <rect x={0} y={0} width={w} height={h} rx={rx} fill="#7a5b2f" stroke={stroke} strokeWidth={Math.max(w, h) * 0.012} />
      {split ? (
        <>
          <rect x={bowlPad} y={bowlPad} width={Math.max(mid - bowlPad * 1.5, 1)} height={h - bowlPad * 2} rx={w * 0.03} fill="none" stroke={stroke} strokeWidth={Math.max(w, h) * 0.008} />
          <rect x={mid + bowlPad * 0.5} y={bowlPad} width={Math.max(w - mid - bowlPad * 1.5, 1)} height={h - bowlPad * 2} rx={w * 0.03} fill="none" stroke={stroke} strokeWidth={Math.max(w, h) * 0.008} />
        </>
      ) : null}
      {Array.from({ length: holes }, (_, i) => {
        const cx = w / 2 + (i - (holes - 1) / 2) * spacing;
        return <circle key={i} cx={cx} cy={-holeR * 2.2} r={holeR} fill="none" stroke="#e4e4e7" strokeWidth={Math.max(w, h) * 0.008} />;
      })}
    </svg>
  );
};

const Row = ({ k, v, testId }) => (
  <div className="flex items-center justify-between gap-3 py-1 text-[11px]">
    <span className="text-zinc-500">{k}</span>
    <span className="font-semibold text-zinc-100" data-testid={testId}>{v}</span>
  </div>
);

const Block = ({ k, v }) => (
  <div className="py-1.5 text-[11px]">
    <div className="text-zinc-500">{k}</div>
    <div className="font-semibold text-zinc-100">{v || "-"}</div>
  </div>
);

export const CutoutTemplatePicker = ({
  open,
  onClose,
  templates = [],
  onUse,
  onDelete,
  onRename,
  onUploadPhoto,
  onSetPhoto,
}) => {
  const [selId, setSelId] = useState(null);
  const [editName, setEditName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const selected = templates.find((t) => t.id === selId) || null;
  const close = () => {
    setSelId(null);
    setEditName(null);
    onClose?.();
  };
  const commitRename = () => {
    const name = (editName || "").trim();
    if (name && selected && name !== selected.name) onRename?.(selected.id, name);
    setEditName(null);
  };
  const pickPhoto = async (file) => {
    if (!file || !selected || !onUploadPhoto) return;
    setUploading(true);
    try {
      const path = await onUploadPhoto(file);
      if (path) onSetPhoto?.(selected.id, path);
    } finally {
      setUploading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-w-4xl gap-0 overflow-hidden border-zinc-800 bg-zinc-900 p-0 text-zinc-100 [&>button]:hidden"
        data-testid="cutout-template-picker"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-base font-bold">
            Sink Cutout Templates
            <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] font-bold text-zinc-300" data-testid="cutout-template-count">
              {templates.length}
            </span>
          </DialogTitle>
          <button type="button" onClick={close} data-testid="cutout-template-close" className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="grid min-h-[420px] grid-cols-1 sm:grid-cols-[1fr_300px]">
          <div className="max-h-[62vh] space-y-2 overflow-y-auto p-3" data-testid="cutout-template-list">
            {templates.length === 0 && (
              <p className="p-4 text-xs text-zinc-500" data-testid="cutout-template-empty">
                No saved templates yet. Select a cut-out and choose "Save cut-out as template".
              </p>
            )}
            {templates.map((t) => (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                data-testid={`cutout-template-item-${t.id}`}
                aria-selected={selId === t.id}
                onClick={() => {
                  setSelId(t.id);
                  setEditName(null);
                }}
                onDoubleClick={() => onUse?.(t)}
                onKeyDown={(e) => e.key === "Enter" && setSelId(t.id)}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border bg-zinc-950/60 p-2 transition-colors duration-150 ${
                  selId === t.id ? "border-indigo-400 bg-indigo-500/10" : "border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <div className="flex h-14 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-zinc-800 bg-zinc-900">
                  {t.photo ? (
                    <img src={photoUrl(t.photo)} alt="" className="h-full w-full object-cover" data-testid={`cutout-template-photo-${t.id}`} />
                  ) : (
                    <CutoutThumb t={t} className="h-11 w-20" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-zinc-100">{t.name}</div>
                  <div className="font-mono-data mt-1 text-[11px] text-zinc-400">
                    {formatInches(t.w)} × {formatInches(t.h)} in
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col border-t border-zinc-800 p-4 sm:border-l sm:border-t-0" data-testid="cutout-template-preview">
            {selected ? (
              <>
                {editName === null ? (
                  <div className="truncate text-sm font-bold" data-testid="cutout-template-preview-name">{selected.name}</div>
                ) : (
                  <Input
                    autoFocus
                    data-testid="cutout-template-rename-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      if (e.key === "Escape") setEditName(null);
                    }}
                    className="h-8 border-zinc-700 bg-zinc-950 text-sm"
                  />
                )}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="flex h-28 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950">
                    <CutoutThumb t={selected} className="h-24 w-full" />
                  </div>
                  <button
                    type="button"
                    data-testid="cutout-template-photo-button"
                    onClick={() => fileRef.current?.click()}
                    disabled={!onUploadPhoto || uploading}
                    title="Attach a real sink photo"
                    className="relative flex h-28 items-center justify-center overflow-hidden rounded-lg border border-dashed border-zinc-700 bg-zinc-950 text-zinc-500 transition-colors duration-150 hover:border-indigo-400 hover:text-indigo-200 disabled:opacity-50"
                  >
                    {selected.photo ? (
                      <img src={photoUrl(selected.photo)} alt="" className="h-full w-full object-cover" data-testid="cutout-template-preview-photo" />
                    ) : (
                      <span className="flex flex-col items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
                        <Camera size={18} weight="bold" />
                        {uploading ? "Uploading…" : "Add photo"}
                      </span>
                    )}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="cutout-template-photo-input"
                    onChange={(e) => {
                      pickPhoto(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </div>
                {selected.photo && onSetPhoto && (
                  <button
                    type="button"
                    data-testid="cutout-template-photo-remove"
                    onClick={() => onSetPhoto(selected.id, "")}
                    className="mt-1 self-end text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-red-400"
                  >
                    Remove photo
                  </button>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="cutout-template-edit"
                    onClick={() => setEditName(selected.name || "")}
                    disabled={!onRename}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-zinc-700 py-2 text-xs font-bold text-zinc-200 transition-colors duration-150 hover:bg-zinc-800 disabled:opacity-40"
                  >
                    <PencilSimple size={13} weight="bold" /> Edit
                  </button>
                  <button
                    type="button"
                    data-testid="cutout-template-delete"
                    onClick={() => {
                      onDelete?.(selected.id);
                      setSelId(null);
                    }}
                    disabled={!onDelete}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-red-500/40 py-2 text-xs font-bold text-red-400 transition-colors duration-150 hover:bg-red-500/10 disabled:opacity-40"
                  >
                    <Trash size={13} weight="bold" /> Delete
                  </button>
                </div>
                <div className="mt-3 divide-y divide-zinc-800/60">
                  <Row k="Type" v={selected.sink_type || "Undermount"} />
                  <Row k="Form" v={selected.shape || "Rectangle"} />
                  <Row k="Size (LxW)" v={`${formatInches(selected.w)} × ${formatInches(selected.h)} in`} testId="cutout-template-preview-size" />
                  <Row k="Faucet Holes Count" v={Number(selected.faucet_holes) || 0} />
                  <Row k="Centrelines" v={`${selected.center_h || "None"} + ${selected.center_v || "None"}`} />
                </div>
                <div className="mt-2 border-t border-zinc-800 pt-2">
                  <Block k="Cutout Service" v={selected.kind} />
                  <Block k="Sink Product" v={selected.product} />
                  <Block k="Installation Service" v={selected.install_service} />
                </div>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-indigo-500/15">
                  <ClipboardText size={48} weight="duotone" className="text-indigo-300" />
                </div>
                <div className="mt-4 text-sm font-bold">Nothing to Preview Yet</div>
                <p className="mt-1 text-xs text-zinc-400">Choose a template from the list to preview it here</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
          <button type="button" onClick={close} data-testid="cutout-template-cancel" className="rounded-md px-3 py-2 text-xs font-bold text-zinc-300 hover:bg-zinc-800">
            Cancel
          </button>
          <button
            type="button"
            disabled={!selected}
            data-testid="cutout-template-use"
            onClick={() => selected && onUse?.(selected)}
            className="rounded-md bg-indigo-500 px-4 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
          >
            Use Selected Template
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
